import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { apiGet } from '../api/client';
import { getTotal, getRecipients, getRevoked, getSummary, StatsPeriod, RecipientRow, RevokedRow } from '../api/stats';
import { Badge, ApiResponse } from '@tinyrewards/shared';

const PERIOD_LABELS: Record<StatsPeriod, string> = {
    all: 'All Time',
    today: 'Today',
    week: 'This Week',
    month: 'This Month',
    lweek: 'Last Week',
    lmonth: 'Last Month',
    between: 'Custom Range',
};

interface TabPanelProps { children?: React.ReactNode; value: number; index: number }
function TabPanel({ children, value, index }: TabPanelProps) {
    return value === index ? <Box pt={3}>{children}</Box> : null;
}

function formatDate(d: string | Date | null | undefined) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const PAGE_SIZE = 50;

export default function BadgeMetricsPage() {
    const { id } = useParams<{ id: string }>();
    const [badge, setBadge] = useState<Badge | null>(null);
    const [badgeLoading, setBadgeLoading] = useState(true);
    const [badgeError, setBadgeError] = useState('');

    const [totalIssued, setTotalIssued] = useState<number | null>(null);
    const [totalRevoked, setTotalRevoked] = useState<number | null>(null);
    const [period, setPeriod] = useState<StatsPeriod>('all');
    const [tab, setTab] = useState(0);

    const [recipients, setRecipients] = useState<RecipientRow[]>([]);
    const [recipientsTotal, setRecipientsTotal] = useState(0);
    const [recipientsPage, setRecipientsPage] = useState(1);
    const [loadingRecipients, setLoadingRecipients] = useState(false);

    const [revoked, setRevokedList] = useState<RevokedRow[]>([]);
    const [revokedTotal, setRevokedTotal] = useState(0);
    const [revokedPage, setRevokedPage] = useState(1);
    const [loadingRevoked, setLoadingRevoked] = useState(false);

    // Load badge info
    useEffect(() => {
        if (!id) return;
        setBadgeLoading(true);
        apiGet<Badge>(`/api/badges/${id}`)
            .then((res: ApiResponse<Badge>) => {
                setBadgeLoading(false);
                if (res.success && res.data) setBadge(res.data);
                else setBadgeError('Badge not found.');
            })
            .catch(() => { setBadgeLoading(false); setBadgeError('Failed to load badge.'); });
    }, [id]);

    // Load totals
    useEffect(() => {
        if (!id) return;
        getTotal(id).then(res => { if (res.success && res.data) setTotalIssued(res.data.totalBadgesIssued); });
    }, [id]);

    // Load summary (for revoked total)
    useEffect(() => {
        if (!id) return;
        getSummary(id, period).then(res => {
            if (res.success && res.data) setTotalRevoked(res.data.totalRevoked);
        });
    }, [id, period]);

    const loadRecipients = useCallback(async (page: number, p: StatsPeriod) => {
        if (!id) return;
        setLoadingRecipients(true);
        const res = await getRecipients(id, p, page);
        setLoadingRecipients(false);
        if (res.success && res.data) {
            setRecipients(res.data);
            setRecipientsTotal((res as { total?: number }).total ?? 0);
        }
    }, [id]);

    const loadRevoked = useCallback(async (page: number, p: StatsPeriod) => {
        if (!id) return;
        setLoadingRevoked(true);
        const res = await getRevoked(id, p, page);
        setLoadingRevoked(false);
        if (res.success && res.data) {
            setRevokedList(res.data);
            setRevokedTotal((res as { total?: number }).total ?? 0);
        }
    }, [id]);

    useEffect(() => {
        setRecipientsPage(1);
        setRevokedPage(1);
        loadRecipients(1, period);
        loadRevoked(1, period);
    }, [period, loadRecipients, loadRevoked]);

    const claimChip = (status: string) => {
        const colors: Record<string, 'default' | 'success' | 'error' | 'warning'> = {
            claimed: 'success', pending: 'warning', rejected: 'error', revoked: 'error',
        };
        return <Chip label={status} color={colors[status] ?? 'default'} size="small" />;
    };

    if (badgeError) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{badgeError}</Alert>
                <Button component={Link} to="/badges" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>Back to Badges</Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Button component={Link} to={`/badges/${id}`} startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                Back to Badge
            </Button>

            {/* Badge header */}
            <Box display="flex" alignItems="center" gap={2} mb={4}>
                {badgeLoading ? (
                    <>
                        <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: 1 }} />
                        <Box><Skeleton width={240} height={32} /><Skeleton width={160} /></Box>
                    </>
                ) : badge ? (
                    <>
                        {badge.badgeImage ? (
                            <Box component="img" src={badge.badgeImage} alt={badge.badgeName}
                                sx={{ width: 64, height: 64, borderRadius: 1, objectFit: 'contain', border: '1px solid', borderColor: 'divider' }} />
                        ) : (
                            <WorkspacePremiumIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                        )}
                        <Box>
                            <Typography variant="h5" fontWeight={700}>{badge.badgeName}</Typography>
                            <Typography variant="body2" color="text.secondary">{badge.badgeDescription}</Typography>
                        </Box>
                    </>
                ) : null}
            </Box>

            {/* Summary cards */}
            <Box display="flex" gap={2} mb={4} flexWrap="wrap">
                <Card variant="outlined" sx={{ minWidth: 160 }}>
                    <CardContent sx={{ textAlign: 'center', py: '16px !important' }}>
                        <Typography variant="h4" fontWeight={700} color="primary">
                            {totalIssued === null ? <Skeleton width={60} sx={{ mx: 'auto' }} /> : totalIssued}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Issued</Typography>
                    </CardContent>
                </Card>
                <Card variant="outlined" sx={{ minWidth: 160 }}>
                    <CardContent sx={{ textAlign: 'center', py: '16px !important' }}>
                        <Typography variant="h4" fontWeight={700} color="error">
                            {totalRevoked === null ? <Skeleton width={60} sx={{ mx: 'auto' }} /> : totalRevoked}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Revoked</Typography>
                    </CardContent>
                </Card>
                <Box flexGrow={1} display="flex" alignItems="center" justifyContent="flex-end">
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Period</InputLabel>
                        <Select
                            value={period}
                            label="Period"
                            onChange={e => setPeriod(e.target.value as StatsPeriod)}
                        >
                            {(Object.keys(PERIOD_LABELS) as StatsPeriod[])
                                .filter(p => p !== 'between')
                                .map(p => <MenuItem key={p} value={p}>{PERIOD_LABELS[p]}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label={`Recipients (${recipientsTotal})`} />
                <Tab label={`Revoked (${revokedTotal})`} />
            </Tabs>

            {/* Recipients tab */}
            <TabPanel value={tab} index={0}>
                {loadingRecipients ? (
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                ) : recipients.length === 0 ? (
                    <Box textAlign="center" py={6}>
                        <Typography color="text.secondary">No badges issued in this period.</Typography>
                    </Box>
                ) : (
                    <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Recipient</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Issued</TableCell>
                                    <TableCell>Expires</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recipients.map(r => (
                                    <TableRow key={r.issuedBadgeID}>
                                        <TableCell>{r.recipientName || '—'}</TableCell>
                                        <TableCell>{r.recipientEmail}</TableCell>
                                        <TableCell>{formatDate(r.issuedDate)}</TableCell>
                                        <TableCell>{formatDate(r.expiryDate)}</TableCell>
                                        <TableCell>{claimChip(r.claimStatus)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {recipientsTotal > PAGE_SIZE && (
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Pagination
                                    count={Math.ceil(recipientsTotal / PAGE_SIZE)}
                                    page={recipientsPage}
                                    onChange={(_, p) => { setRecipientsPage(p); loadRecipients(p, period); }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </TabPanel>

            {/* Revoked tab */}
            <TabPanel value={tab} index={1}>
                {loadingRevoked ? (
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                ) : revoked.length === 0 ? (
                    <Box textAlign="center" py={6}>
                        <Typography color="text.secondary">No revoked badges in this period.</Typography>
                    </Box>
                ) : (
                    <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Recipient</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Issued</TableCell>
                                    <TableCell>Revoked</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {revoked.map(r => (
                                    <TableRow key={r.issuedBadgeID}>
                                        <TableCell>{r.recipientName || '—'}</TableCell>
                                        <TableCell>{r.recipientEmail}</TableCell>
                                        <TableCell>{formatDate(r.issuedDate)}</TableCell>
                                        <TableCell>{formatDate(r.revokedDate)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {revokedTotal > PAGE_SIZE && (
                            <Box display="flex" justifyContent="center" mt={3}>
                                <Pagination
                                    count={Math.ceil(revokedTotal / PAGE_SIZE)}
                                    page={revokedPage}
                                    onChange={(_, p) => { setRevokedPage(p); loadRevoked(p, period); }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </TabPanel>
        </Container>
    );
}
